import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAPI } from '../../API';
import { ActionTypes, useAppDispatch, useAppState } from '../../Store';
import { useTranslation } from '../../Translation';
import SidebarNav from '../../Components/SidebarNav/SidebarNav';
import useAuthenticated from '../Dashboard/useAuthenticated';
import Table from '../../Components/Table/Table';
import LoadingCircleTransparent from '../../Components/LoadingIndicators/LoadingCircleTransparent';
import { usePaginations } from '../../Hooks/usePaginations';
import { useDateFormat } from '../../Hooks/useDateFormat';
import { automatisationStepBackground, iteration, split, thunder, webhook } from '../../Resources/Images';
import { ArrowsPointingOutIcon, ChevronDownIcon, ChevronUpIcon, DocumentPlusIcon, EllipsisHorizontalCircleIcon, EllipsisHorizontalIcon, MagnifyingGlassMinusIcon, MagnifyingGlassPlusIcon, ClockIcon, TagIcon, CalculatorIcon, CalendarIcon, ArrowRightStartOnRectangleIcon } from '@heroicons/react/24/outline';
import Panel, { usePanel } from '../../Components/Panel';
import APICallStep from '../../Components/AutomatisationSteps/APICall/APICallStep';
import { useRefresh } from '../../Hooks/useRefresh';
import ConditionStep from '../../Components/AutomatisationSteps/Condition/ConditionStep';
import DelayStep from '../../Components/AutomatisationSteps/Delay/DelayStep';
import UpdateVariableStep from '../../Components/AutomatisationSteps/UpdateVariable/UpdateVariableStep';
import TagStep from '../../Components/AutomatisationSteps/Tag/TagStep';
import LoopStep from '../../Components/AutomatisationSteps/Loop/LoopStep';
import { ArrowDownOnSquareStackIcon, PlusCircleIcon, PlusIcon, VariableIcon } from '@heroicons/react/24/solid';
import { hoverTextColor, textColor } from '../../Resources/CSS/styling';
import APICallPanel from '../../Components/AutomatisationSteps/APICall/APICallPanel';
import ConditionPanel from '../../Components/AutomatisationSteps/Condition/ConditionPanel';
import DelayPanel from '../../Components/AutomatisationSteps/Delay/DelayPanel';
import UpdateVariablePanel from '../../Components/AutomatisationSteps/UpdateVariable/UpdateVariablePanel';
import TagPanel from '../../Components/AutomatisationSteps/Tag/TagPanel';
import LoopPanel from '../../Components/AutomatisationSteps/Loop/LoopPanel';
import { Button } from '../../Components/Buttons';
import Select from '../../Components/Inputs/Select';
import { TextInput } from '../../Components/Inputs/TextInput';
import Modal from '../../Components/Modal';
import { AutomatisationModal, getTagEventName } from './Automatisations';
import Badge from '../../Components/Badge';
import TwilioSMSPanel from '../../Components/AutomatisationSteps/App/Twilio/SMS/TwilioSMSPanel';
import TwilioSMSStep from '../../Components/AutomatisationSteps/App/Twilio/SMS/TwilioSMSStep';
import SendGridEmailPanel from '../../Components/AutomatisationSteps/App/SendGrid/Email/SendGridEmailPanel';
import SendGridEmailStep from '../../Components/AutomatisationSteps/App/SendGrid/Email/SendGridEmailStep';
import OneSignalPushPanel from '../../Components/AutomatisationSteps/App/OneSignal/Push/OneSignalPushPanel';
import OneSignalPushStep from '../../Components/AutomatisationSteps/App/OneSignal/Push/OneSignalPushStep';
import ChatGPTPromptPanel from '../../Components/AutomatisationSteps/App/ChatGPT/Prompt/ChatGPTPromptPanel';
import ChatGPTPromptStep from '../../Components/AutomatisationSteps/App/ChatGPT/Prompt/ChatGPTPromptStep';
import GeminiPromptPanel from '../../Components/AutomatisationSteps/App/Gemini/Prompt/GeminiPromptPanel';
import GeminiPromptStep from '../../Components/AutomatisationSteps/App/Gemini/Prompt/GeminiPromptStep';
import SlackMessagePanel from '../../Components/AutomatisationSteps/App/Slack/Message/SlackMessagePanel';
import SlackMessageStep from '../../Components/AutomatisationSteps/App/Slack/Message/SlackMessageStep';
import GmailEmailPanel from '../../Components/AutomatisationSteps/App/Gmail/Email/GmailEmailPanel';
import GmailEmailStep from '../../Components/AutomatisationSteps/App/Gmail/Email/GmailEmailStep';
import TrackPanel from '../../Components/AutomatisationSteps/Track/TrackPanel';
import TrackStep from '../../Components/AutomatisationSteps/Track/TrackStep';
import { DocumentDuplicateIcon } from '@heroicons/react/24/solid';
import Switch from '../../Components/Switch';
import SegmentControl from '../../Components/SegmentControl';
import { useTabs } from '../../Components/Tabs';
import CountStep from '../../Components/AutomatisationSteps/Count/CountStep';
import CountPanel from '../../Components/AutomatisationSteps/Count/CountPanel';
import MetaAudiencePanel from '../../Components/AutomatisationSteps/App/Meta/Audience/MetaAudiencePanel';
import MetaAudienceStep from '../../Components/AutomatisationSteps/App/Meta/Audience/MetaAudienceStep';
import DatePanel from '../../Components/AutomatisationSteps/Date/DatePanel';
import DateStep from '../../Components/AutomatisationSteps/Date/DateStep';
import OllamaPromptPanel from '../../Components/AutomatisationSteps/App/Ollama/Prompt/OllamaPromptPanel';
import OllamaPromptStep from '../../Components/AutomatisationSteps/App/Ollama/Prompt/OllamaPromptStep';
import LlamaPromptPanel from '../../Components/AutomatisationSteps/App/Llama/Prompt/LlamaPromptPanel';
import LlamaPromptStep from '../../Components/AutomatisationSteps/App/Llama/Prompt/LlamaPromptStep';
import GoToStep from '../../Components/AutomatisationSteps/GoTo/GoToStep';
import GoToPanel from '../../Components/AutomatisationSteps/GoTo/GoToPanel';

export const hardcodedAppsWebhook = [
    {
        name: 'Twilio',
        webhooks: [
            {
                name: 'SMS',
                description: 'Send an SMS to a contact',
                webhook_unique_identifier: 'TwilioSMS',
                onClick: (panelHook, onCreateNewStep, refresh, automatisationId, previousStep, app, webhook, userApp) => {
                    panelHook.setOpen(false);
                    setTimeout(() => {
                        panelHook.setOpen(true, <TwilioSMSPanel 
                            onCreateNewStep={onCreateNewStep} 
                            panelHook={panelHook} 
                            step={{type: 'APICALL', automatisation_step_before_id: previousStep?.id, automatisation_id: automatisationId, data: {body: {}, headers: {}, method: 'GET', url: ''}}} 
                            refresh={refresh} 
                            isCreate={true} 
                            app={app} 
                            appWebhookStep={webhook} 
                            userApp={userApp} />);
                    }, 1000);
                }
            },
            
        ]
    },
    {
        name: 'SendGrid',
        webhooks: [
            {
                name: 'Email',
                description: 'Send an email to a contact',
                webhook_unique_identifier: 'SendGridEmail',
                onClick: (panelHook, onCreateNewStep, refresh, automatisationId, previousStep, app, webhook, userApp) => {
                    panelHook.setOpen(false);
                    setTimeout(() => {
                        panelHook.setOpen(true, <SendGridEmailPanel 
                            onCreateNewStep={onCreateNewStep} 
                            panelHook={panelHook} 
                            step={{type: 'APICALL', automatisation_step_before_id: previousStep?.id, automatisation_id: automatisationId, data: {body: {}, headers: {}, method: 'GET', url: ''}}} 
                            refresh={refresh} 
                            isCreate={true} 
                            app={app} 
                            appWebhookStep={webhook} 
                            userApp={userApp} />);
                    }, 1000);
                }
            }
        ]
    },
    {
        name: 'OneSignal',
        webhooks: [
            {
                name: 'Push',
                description: 'Send a push notification to a user',
                webhook_unique_identifier: 'OneSignalPush',
                onClick: (panelHook, onCreateNewStep, refresh, automatisationId, previousStep, app, webhook, userApp) => {
                    panelHook.setOpen(false);
                    setTimeout(() => {
                        panelHook.setOpen(true, <OneSignalPushPanel 
                            onCreateNewStep={onCreateNewStep} 
                            panelHook={panelHook} 
                            step={{type: 'APICALL', automatisation_step_before_id: previousStep?.id, automatisation_id: automatisationId, data: {body: {}, headers: {}, method: 'GET', url: ''}}} 
                            refresh={refresh} 
                            isCreate={true} 
                            app={app} 
                            appWebhookStep={webhook} 
                            userApp={userApp} />
                        );
                    }, 1000);
                }
            }
        ]
    },
    {
        name: 'OpenAI',
        webhooks: [
            {
                name: 'Prompt',
                description: 'Send a prompt to ChatGPT',
                webhook_unique_identifier: 'ChatGPTPrompt',
                onClick: (panelHook, onCreateNewStep, refresh, automatisationId, previousStep, app, webhook, userApp) => {
                    panelHook.setOpen(false);
                    setTimeout(() => {
                        panelHook.setOpen(true, <ChatGPTPromptPanel 
                            onCreateNewStep={onCreateNewStep} 
                            panelHook={panelHook} 
                            step={{type: 'APICALL', automatisation_step_before_id: previousStep?.id, automatisation_id: automatisationId, data: {body: {}, headers: {}, method: 'GET', url: ''}}} 
                            refresh={refresh} 
                            isCreate={true} 
                            app={app} 
                            appWebhookStep={webhook} 
                            userApp={userApp} />
                        );
                    }, 1000);
                }
            }
        ]
    },
    {
        name: 'Gemini',
        webhooks: [
            {
                name: 'Prompt',
                description: 'Send a prompt to Gemini',
                webhook_unique_identifier: 'GeminiPrompt',
                onClick: (panelHook, onCreateNewStep, refresh, automatisationId, previousStep, app, webhook, userApp) => {
                    panelHook.setOpen(false);
                    setTimeout(() => {
                        panelHook.setOpen(true, <GeminiPromptPanel 
                            onCreateNewStep={onCreateNewStep} 
                            panelHook={panelHook} 
                            step={{type: 'APICALL', automatisation_step_before_id: previousStep?.id, automatisation_id: automatisationId, data: {body: {}, headers: {}, method: 'GET', url: ''}}} 
                            refresh={refresh} 
                            isCreate={true} 
                            app={app} 
                            appWebhookStep={webhook} 
                            userApp={userApp} />
                        );
                    }, 1000);
                }
            }
        ]
    },
    {
        name: 'Slack',
        webhooks: [
            {
                name: 'Message',
                description: 'Send a message to a channel',
                webhook_unique_identifier: 'SlackMessage',
                onClick: (panelHook, onCreateNewStep, refresh, automatisationId, previousStep, app, webhook, userApp) => {
                    panelHook.setOpen(false);
                    setTimeout(() => {
                        panelHook.setOpen(true, <SlackMessagePanel 
                            onCreateNewStep={onCreateNewStep} 
                            panelHook={panelHook} 
                            step={{type: 'APICALL', automatisation_step_before_id: previousStep?.id, automatisation_id: automatisationId, data: {body: {}, headers: {}, method: 'GET', url: ''}}} 
                            refresh={refresh} 
                            isCreate={true} 
                            app={app} 
                            appWebhookStep={webhook} 
                            userApp={userApp} />
                        );
                    }, 1000);
                }
            }
        ]
    },
    {
        name: 'Gmail',
        webhooks: [
            {
                name: 'Email',
                description: 'Send an email to a contact',
                webhook_unique_identifier: 'GmailEmail',
                onClick: (panelHook, onCreateNewStep, refresh, automatisationId, previousStep, app, webhook, userApp) => {
                    panelHook.setOpen(false);
                    setTimeout(() => {
                        panelHook.setOpen(true, <GmailEmailPanel 
                            onCreateNewStep={onCreateNewStep} 
                            panelHook={panelHook} 
                            step={{type: 'APICALL', automatisation_step_before_id: previousStep?.id, automatisation_id: automatisationId, data: {body: {}, headers: {}, method: 'GET', url: ''}}} 
                            refresh={refresh} 
                            isCreate={true} 
                            app={app} 
                            appWebhookStep={webhook} 
                            userApp={userApp} />
                        );
                    }, 1000);
                }
            }
        ]
    },
    {
        name: 'Meta',
        webhooks: [
            {
                name: 'Audience',
                description: 'Add a user to a Meta audience',
                webhook_unique_identifier: 'MetaAudience',
                onClick: (panelHook, onCreateNewStep, refresh, automatisationId, previousStep, app, webhook, userApp) => {
                    panelHook.setOpen(false);
                    setTimeout(() => {
                        panelHook.setOpen(true, <MetaAudiencePanel 
                            onCreateNewStep={onCreateNewStep} 
                            panelHook={panelHook} 
                            step={{type: 'APICALL', automatisation_step_before_id: previousStep?.id, automatisation_id: automatisationId, data: {body: {}, headers: {}, method: 'GET', url: ''}}} 
                            refresh={refresh} 
                            isCreate={true} 
                            app={app} 
                            appWebhookStep={webhook} 
                            userApp={userApp} />
                        );
                    }, 1000);
                }
            }
        ]
    },
    {
        name: 'Ollama',
        webhooks: [
            {
                name: 'Prompt',
                description: 'Send a prompt to Ollama',
                webhook_unique_identifier: 'OllamaPrompt',
                onClick: (panelHook, onCreateNewStep, refresh, automatisationId, previousStep, app, webhook, userApp) => {
                    panelHook.setOpen(false);
                    setTimeout(() => {
                        panelHook.setOpen(true, <OllamaPromptPanel
                            onCreateNewStep={onCreateNewStep} 
                            panelHook={panelHook} 
                            step={{type: 'APICALL', automatisation_step_before_id: previousStep?.id, automatisation_id: automatisationId, data: {body: {}, headers: {}, method: 'GET', url: ''}}} 
                            refresh={refresh} 
                            isCreate={true} 
                            app={app} 
                            appWebhookStep={webhook} 
                            userApp={userApp} />
                        );
                    }, 1000);
                }
            }
        ]
    },
    {
        name: 'Llama',
        webhooks: [
            {
                name: 'Prompt',
                description: 'Send a prompt to Llama Server',
                webhook_unique_identifier: 'LlamaPrompt',
                onClick: (panelHook, onCreateNewStep, refresh, automatisationId, previousStep, app, webhook, userApp) => {
                    panelHook.setOpen(false);
                    setTimeout(() => {
                        panelHook.setOpen(true, <LlamaPromptPanel
                            onCreateNewStep={onCreateNewStep} 
                            panelHook={panelHook} 
                            step={{type: 'APICALL', automatisation_step_before_id: previousStep?.id, automatisation_id: automatisationId, data: {body: {}, headers: {}, method: 'GET', url: ''}}} 
                            refresh={refresh} 
                            isCreate={true} 
                            app={app} 
                            appWebhookStep={webhook} 
                            userApp={userApp} />
                        );
                    }, 1000);
                }
            }
        ]
    }
]

export const LineDown = ({withPlus = false, action = null, stepToClone = null, previousCloneStep = null, nextCloneStep = null, cloneAction = null, isFirstTrueSubStep = false, isFirstFalseSubStep = false}) => {


    
    return (
        <div className="flex flex-col items-center justify-center my-4">
            <div className={`w-0.5 bg-gray-300 ${!withPlus ? 'h-16 min-h-16' : 'h-8 min-h-8'}`} />
            {withPlus && stepToClone && <ArrowDownOnSquareStackIcon className={`size-8 my-1 text-gray-400 ${stepToClone ? 'animate-flash' : ''} cursor-pointer ${hoverTextColor}`} onClick={() => {
                cloneAction(previousCloneStep, nextCloneStep, isFirstTrueSubStep, isFirstFalseSubStep);
            }} />}
            {withPlus && !stepToClone && <PlusCircleIcon className={`size-8 my-1 text-gray-400 ${stepToClone ? 'animate-flash' : ''} cursor-pointer ${hoverTextColor}`} onClick={() => {
               action(); 
            }} />}
            {withPlus && <div className="w-0.5 h-8 min-h-8 bg-gray-300 animate-flash" />}
        </div>
        
    )
}

export const AutomatisationStep = ({automatisationId, panelHook, step, refresh, deleteStep, app, userApp, appWebhookStep, apps, userApps, setApps, setStepToClone, stepToClone, cloneAction}) => {
    const { translate } = useTranslation();

    if (step.type.toUpperCase() === 'APICALL') {
        if (step.app_webhook_unique_identifier === 'TwilioSMS') {
            return <TwilioSMSStep setStepToClone={setStepToClone} panelHook={panelHook} step={step} refresh={refresh} deleteStep={deleteStep} app={app} userApp={userApp} appWebhookStep={appWebhookStep} />
        } else if (step.app_webhook_unique_identifier === 'SendGridEmail') {
            return <SendGridEmailStep setStepToClone={setStepToClone} panelHook={panelHook} step={step} refresh={refresh} deleteStep={deleteStep} app={app} userApp={userApp} appWebhookStep={appWebhookStep} />
        } else if (step.app_webhook_unique_identifier === 'OneSignalPush') {
            return <OneSignalPushStep setStepToClone={setStepToClone} panelHook={panelHook} step={step} refresh={refresh} deleteStep={deleteStep} app={app} userApp={userApp} appWebhookStep={appWebhookStep} />
        } else if (step.app_webhook_unique_identifier === 'ChatGPTPrompt') {
            return <ChatGPTPromptStep setStepToClone={setStepToClone} panelHook={panelHook} step={step} refresh={refresh} deleteStep={deleteStep} app={app} userApp={userApp} appWebhookStep={appWebhookStep} />
        } else if (step.app_webhook_unique_identifier === 'GeminiPrompt') {
            return <GeminiPromptStep setStepToClone={setStepToClone} panelHook={panelHook} step={step} refresh={refresh} deleteStep={deleteStep} app={app} userApp={userApp} appWebhookStep={appWebhookStep} />
        } else if (step.app_webhook_unique_identifier === 'SlackMessage') {
            return <SlackMessageStep setStepToClone={setStepToClone} panelHook={panelHook} step={step} refresh={refresh} deleteStep={deleteStep} app={app} userApp={userApp} appWebhookStep={appWebhookStep} />
        } else if (step.app_webhook_unique_identifier === 'GmailEmail') {
            return <GmailEmailStep setStepToClone={setStepToClone} panelHook={panelHook} step={step} refresh={refresh} deleteStep={deleteStep} app={app} userApp={userApp} appWebhookStep={appWebhookStep} />
        } else if (step.app_webhook_unique_identifier === 'MetaAudience') {
            return <MetaAudienceStep setStepToClone={setStepToClone} panelHook={panelHook} step={step} refresh={refresh} deleteStep={deleteStep} app={app} userApp={userApp} appWebhookStep={appWebhookStep} />
        } else if (step.app_webhook_unique_identifier === 'OllamaPrompt') {
            return <OllamaPromptStep setStepToClone={setStepToClone} panelHook={panelHook} step={step} refresh={refresh} deleteStep={deleteStep} app={app} userApp={userApp} appWebhookStep={appWebhookStep} />
        } else if (step.app_webhook_unique_identifier === 'LlamaPrompt') {
            return <LlamaPromptStep setStepToClone={setStepToClone} panelHook={panelHook} step={step} refresh={refresh} deleteStep={deleteStep} app={app} userApp={userApp} appWebhookStep={appWebhookStep} />
        } else {
            return <APICallStep setStepToClone={setStepToClone} panelHook={panelHook} step={step} refresh={refresh} deleteStep={deleteStep} />
        }
    } else if (step.type.toUpperCase() === 'CONDITION') {
        return <ConditionStep cloneAction={cloneAction} stepToClone={stepToClone} setStepToClone={setStepToClone} automatisationId={automatisationId} panelHook={panelHook} step={step} refresh={refresh} deleteStep={deleteStep} apps={apps} userApps={userApps} setApps={setApps} />
    } else if (step.type.toUpperCase() === 'DELAY') {
        if (step.data.date || step.data.use_date_variable) {
            return <DateStep setStepToClone={setStepToClone} panelHook={panelHook} step={step} refresh={refresh} deleteStep={deleteStep} />
        }
        return <DelayStep setStepToClone={setStepToClone} panelHook={panelHook} step={step} refresh={refresh} deleteStep={deleteStep} />
    } else if (step.type.toUpperCase() === 'UPDATE_VARIABLE') {
        return <UpdateVariableStep setStepToClone={setStepToClone} panelHook={panelHook} step={step} refresh={refresh} deleteStep={deleteStep} />
    } else if (step.type.toUpperCase() === 'TAG') {
        return <TagStep setStepToClone={setStepToClone} panelHook={panelHook} step={step} refresh={refresh} deleteStep={deleteStep} />
    } else if (step.type.toUpperCase() === 'LOOP') {
        return <LoopStep setStepToClone={setStepToClone} panelHook={panelHook} step={step} refresh={refresh} deleteStep={deleteStep} />
    } else if (step.type.toUpperCase() === 'TRACK') {
        return <TrackStep setStepToClone={setStepToClone} panelHook={panelHook} step={step} refresh={refresh} deleteStep={deleteStep} />
    } else if (step.type.toUpperCase() === 'COUNT') {
        return <CountStep setStepToClone={setStepToClone} panelHook={panelHook} step={step} refresh={refresh} deleteStep={deleteStep} />
    } else if (step.type.toUpperCase() === 'GOTO') {
        return <GoToStep setStepToClone={setStepToClone} panelHook={panelHook} step={step} refresh={refresh} deleteStep={deleteStep} automatisationId={automatisationId} />
    }

    return (
        <div className="flex flex-col border-1 w-max h-min border-gray-800 rounded-md p-4 bg-white">
            <h2 className="text-2xl font-bold">{translate('automatisation.step')}</h2>
            <p className="text-xl text-gray-500">{step.type}</p>
        </div>
    )
}

const useAutomatisationStepMapping = (steps) => {
    const [stepMapped, setStepMapped] = useState([]);

    useEffect(() => {

        const createStepList = (startStep, stepMapped) => {
            var currentStep = startStep;
            let stopLoop = false;
            
            while (!stopLoop) {
                if (currentStep) {
                    // When first step is a condition, we dont use the automatisation_step_before_id to find the next step, it will be the true or false next step id
                    if (currentStep.type.toUpperCase() !== 'CONDITION') {
                        currentStep = steps.find((step) => step.automatisation_step_before_id === currentStep.id);
                    }
                } else {
                    currentStep = steps.find((step) => step.automatisation_step_before_id === null);
                }
                

                if (currentStep) {

                    if (currentStep.type.toUpperCase() === 'CONDITION') {
                        stopLoop = true;

                        const trueFirstStep = steps.find((step) => step.id === currentStep.data.results.true_next_step_id);
                        const falseFirstStep = steps.find((step) => step.id === currentStep.data.results.false_next_step_id);

                        if (trueFirstStep) {
                            currentStep.trueSubSteps = createStepList(trueFirstStep, trueFirstStep.type.toUpperCase() !== 'CONDITION' ? [trueFirstStep] : []);
                        }

                        if (falseFirstStep) {
                            currentStep.falseSubSteps = createStepList(falseFirstStep, falseFirstStep.type.toUpperCase() !== 'CONDITION' ? [falseFirstStep] : []);
                        }

                    }


                    stepMapped.push(currentStep);
                } else {
                    stopLoop = true;
                }
            }

            return stepMapped;
        }


        var stepMapped = [];
        stepMapped = createStepList(null, stepMapped);

        setStepMapped(stepMapped);
    }, [steps]);

    return {stepMapped, setStepMapped};
}


export default function Automatisation({isCampaign = false}) {

    const { translate, language, setLanguage } = useTranslation();

    const { isAuthenticated, pathAfterLogin } = useAuthenticated();

    const api = useAPI();

    const [automatisation, setAutomatisation] = useState();
    const [automatisationSteps, setAutomatisationSteps] = useState([]);
    const [zoom, setZoom] = useState(1);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const panelHook = usePanel();
    const [isExitStrategyModalOpen, setIsExitStrategyModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const scrollContainerRef = useRef(null);
    const {refreshId, refresh} = useRefresh();

    const [tags, setTags] = useState([]);

    const {stepMapped, setStepMapped} = useAutomatisationStepMapping(automatisationSteps);
    const appState = useAppState();
    const dispatch = useAppDispatch();
    const automatisationId = useParams().automatisationId;

    const [apps, setApps] = useState([]);
    const [userApps, setUserApps] = useState([]);

    const [stepToClone, setStepToClone] = useState(null);

    

    useEffect(() => {
        const fetchApps = async () => {
            const response = await api.listApps(1, 1000000);
            const responseUserApps = await api.listUserApps(1, 1000000);
            setApps(response.data.apps.sort((a, b) => a.name.localeCompare(b.name)));
            setUserApps(responseUserApps.data.user_apps);
        }
        fetchApps();
    }, []);

    const exitTag = useMemo(() => {
        if (tags && tags.length > 0 && automatisation?.exit_strategy_value) {
            return tags.find((tag) => tag.id === parseInt(automatisation?.exit_strategy_value));
        }
        return null;
    }, [tags, automatisation]);

    const setExitTag = async (tag) => {
        const automatisationToUpdate = {...automatisation, exit_strategy_value: `${tag?.id}`};
        setAutomatisation(automatisationToUpdate);

    }

    const saveExitStrategy = async () => {
        try {
            const responseAutomation = await api.updateAutomatisation(automatisation);
            setAutomatisation(responseAutomation.data.automatisation);
            setIsExitStrategyModalOpen(false);
        } catch (error) {
            console.error(error);
        }
    }

    const publishAutomatisation = async () => {
        try {
            const automatisationToUpdate = {...automatisation, is_active: !automatisation.is_active};
            const responseAutomation = await api.updateAutomatisation(automatisationToUpdate);
            setAutomatisation(responseAutomation.data.automatisation);
            refresh();
        } catch (error) {
            console.error(error);
        }
    }

    const exitStategyTypes = [
        {name: translate('automatisation.exit_strategy_tag'), value: 'tag'},
        {name: translate('automatisation.exit_strategy_event'), value: 'event'}
    ];

    const selectedExitStrategyType = exitStategyTypes.find((type) => type.value === automatisation?.exit_strategy_type);

    const setSelectedExitStrategyType = (type) => {
        setAutomatisation({...automatisation, exit_strategy_type: type?.value, exit_strategy_value: null});
    }

    const clearExitStrategy = async () => {
        const automatisationToUpdate = {...automatisation, exit_strategy_type: null, exit_strategy_value: null};
        setAutomatisation(automatisationToUpdate);

        try {
            const responseAutomation = await api.updateAutomatisation(automatisationToUpdate);
            setAutomatisation(responseAutomation.data.automatisation);
            setIsExitStrategyModalOpen(false);
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        const fetchAutomatisation = async () => {
            try {
                setLoading(true);
                const response = await api.fetchAutomatisation(automatisationId);
                setAutomatisation(response.data.automatisation);

                const responseSteps = await api.fetchAutomatisationSteps(automatisationId);
                setAutomatisationSteps(responseSteps.data.automatisation_steps);

                const responseTags = await api.listTags(1, 1000000);
                setTags(responseTags.data.tags);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        }

        fetchAutomatisation();
    }, [automatisationId, refreshId]);

    // Set initial scroll position
    useEffect(() => {
        if (scrollContainerRef.current && !loading) {
            // Set the initial scroll position (e.g., 500px from the left)
            scrollContainerRef.current.scrollLeft = 1024;
        }
    }, [loading, automatisationSteps]);

    const updateZoom = (value) => {
        if (zoom + value <= 2 && zoom + value >= 0.2) {
            setZoom(zoom + value);
        }
    }

    const deleteStep = (step, flowToKeep = 'true') => {

        const previousStep = automatisationSteps.find((s) => step.automatisation_step_before_id === s.id);
        const nextStep = automatisationSteps.find((s) => step.id === s.automatisation_step_before_id);


        if (nextStep && previousStep && step.type.toUpperCase() !== 'CONDITION') {
            nextStep.automatisation_step_before_id = previousStep.id;

            if (previousStep.type.toUpperCase() === 'CONDITION') {
                if (previousStep.data.results.true_next_step_id === step.id) {
                    previousStep.data.results.true_next_step_id = nextStep.id;
                } else if (previousStep.data.results.false_next_step_id === step.id) {
                    previousStep.data.results.false_next_step_id = nextStep.id;
                }

                api.updateAutomatisationStep(automatisationId, previousStep);
            }

            api.updateAutomatisationStep(automatisationId, nextStep);

        }

        if (!previousStep && nextStep) {
            nextStep.automatisation_step_before_id = null;
            api.updateAutomatisationStep(automatisationId, nextStep);

        }

        if (previousStep && step.type.toUpperCase() === 'CONDITION') {
            // Harcoded true flow is kept for the moment, but we need to allow user to choose between true, false or none flow.
            if (step.data.results?.true_next_step_id && flowToKeep === 'true') {
                const trueFirstStep = automatisationSteps.find((s) => s.id === step.data.results.true_next_step_id);

                if (trueFirstStep) {
                    trueFirstStep.automatisation_step_before_id = previousStep.id;
                    api.updateAutomatisationStep(automatisationId, trueFirstStep);
                }

            }

            if (step.data.results?.false_next_step_id && flowToKeep === 'false') {
                const falseFirstStep = automatisationSteps.find((s) => s.id === step.data.results.false_next_step_id);
                if (falseFirstStep) {
                    falseFirstStep.automatisation_step_before_id = previousStep.id;
                    api.updateAutomatisationStep(automatisationId, falseFirstStep);
                }

            }
        }

        api.deleteAutomatisationStep(automatisationId, step.id);
        panelHook.setOpen(false);
        setTimeout(() => {
            refresh();
        }, 1000);
    }

    const runCampaign = async () => {
        const event = automatisation?.entry_event_name;
        const clientKey = appState.user.client_key; 

        try {
            const response = await api.runCampaign(event, clientKey);

            dispatch({ type: ActionTypes.SET_NOTIFICATION, payload: { show: true, message:  translate('automatisation.campaign_started'), type: 'success', title:  translate('automatisation.success') } });
            
        } catch (error) {
            console.error(error);
        }
    }

    const stopCampaign = async () => {
        const automatistionId = automatisation.id;

        try {
            const response = await api.stopCampaign(automatistionId);
            const countOfCampaignsStopped = response.data.count_of_campaign_stopped;
            dispatch({ type: ActionTypes.SET_NOTIFICATION, payload: { show: true, message:  translate('automatisation.campaign_stopped').replace('%1$d', countOfCampaignsStopped), type: 'success', title:  translate('automatisation.success') } });
        } catch (error) {
            console.error(error);
        }
    }

    const cloneStep = async (previousStep, nextStep, isFirstTrueSubStep, isFirstFalseSubStep) => {
        const newStep = {...stepToClone, id: null, automatisation_step_before_id: !previousStep ? null : previousStep.id};
        // TODO

        console.log('Cloning', newStep, previousStep, nextStep);

        const response = await api.createAutomatisationStep(automatisationId, newStep);
        const newStepResponse = response.data.automatisation_step;

        if (newStepResponse) {
            let needRefresh = false;

            if (previousStep && previousStep.type.toUpperCase() === 'CONDITION') {
                if (isFirstTrueSubStep) {
                    previousStep.data.results.true_next_step_id = newStepResponse.id;
                    await api.updateAutomatisationStep(automatisationId, previousStep);
                } else if (isFirstFalseSubStep) {
                    previousStep.data.results.false_next_step_id = newStepResponse.id;
                    await api.updateAutomatisationStep(automatisationId, previousStep);
                }
            }
        
            if (nextStep) {
                nextStep.automatisation_step_before_id = newStepResponse.id;
                await api.updateAutomatisationStep(automatisationId, nextStep);
                needRefresh = true;
            } 

            refresh();
        }

        setStepToClone(null);
    }

    const entryEventNameFormatted = getTagEventName(automatisation?.entry_event_name, tags, translate);

    return (
        <div className="flex h-screen w-full">
            <Modal
                open={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                title={translate('automatisation.edit')}
                description={''}
                children={<AutomatisationModal refresh={refresh} setOpen={setIsEditModalOpen} automatisation={automatisation} setSelectedAutomatisation={setAutomatisation} open={isEditModalOpen} tags={tags} />}
            />
            <Modal 
                title={translate('automatisation.exit_strategy')} 
                open={isExitStrategyModalOpen} 
                onClose={() => setIsExitStrategyModalOpen(false)}
                description={translate('automatisation.exit_strategy_description')}
                children={<div className="bg-white py-2 rounded-md">
                    <div className="flex flex-col justify-center gap-2 max-w-96">
                        <div className="flex flex-row items-center ">
                            <div className="flex flex-col min-w-48 max-w-48 justify-center">
                                <Select title={''} options={exitStategyTypes} selected={selectedExitStrategyType} setSelected={setSelectedExitStrategyType} />
                            </div>
                        </div>
                        {selectedExitStrategyType?.value === 'tag' && <div className="flex flex-row items-center ">
                            <div className="flex flex-col min-w-48 max-w-48 justify-center">
                                <Select title={''} options={tags} selected={exitTag} setSelected={setExitTag} />
                            </div>
                        </div>}
                        {selectedExitStrategyType?.value === 'event' && <div className="flex flex-row items-center ">
                            <div className="flex flex-col min-w-48 max-w-48 justify-center">
                                <TextInput title={translate('automatisation.exit_strategy_event_name')} value={automatisation?.exit_strategy_value} onChange={(value) => setAutomatisation({...automatisation, exit_strategy_value: value.target.value})} />
                            </div>
                        </div>}
                        <div className="flex flex-row items-center justify-end mt-4">
                            <Button className="bg-red-500 text-white" onClick={clearExitStrategy}>{translate('clear')}</Button>
                            <Button className="bg-green-500 text-white ml-2" onClick={saveExitStrategy}>{translate('save')}</Button>
                        </div>
                      
                    </div>
    
                </div>}
             />
            <SidebarNav currentPage={isCampaign ? "campaigns" : "automatisations"} contentPadding={false}>
                {loading && automatisationSteps.length === 0 ? <div className="flex justify-center items-center h-full w-12">
                    <LoadingCircleTransparent />
                </div> : (
                    <div
                        ref={scrollContainerRef}
                        className={`flex flex-col w-full h-full overflow-scroll`}
                        style={{
                            backgroundImage: `url(${automatisationStepBackground})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat'
                        }}>
                        <div className="flex flex-col w-full h-full items-center ml-[1024px]" style={{ transform: `scale(${zoom})`, marginTop: `${500 * (zoom - 1)}px` }} >
                            {!isCampaign && <div className="flex flex-col border-1 w-max h-min border-gray-300 border w-72 min-w-72 rounded-md p-4 bg-white mt-8">
                                <div className="flex flex-row items-center border-b border-gray-300 pb-2">
                                    <div>
                                        <img src={thunder} alt="condition" className="w-12 h-12 min-w-12 min-h-12 aspect-square object-contain border border-gray-200 p-2 bg-gray-50 rounded-md cursor-pointer mr-2" />
                                    </div>
                                    <div className="flex flex-col justify-center ml-2 w-full -mb-2">
                                        <div className="flex flex-row items-center justify-between w-full h-4 max-h-4">
                                            <h2 className="text-xl font-bold mr-4">{translate('automatisation.event')}</h2>
                                            <EllipsisHorizontalIcon onClick={() => setIsEditModalOpen(true)} className="w-8 -ml-2 text-gray-400 cursor-pointer" />
                                        </div> 
                                        <p className="text-md font-medium text-gray-500 mb-2">{entryEventNameFormatted}</p>
                                        
                                    </div>
                                </div>
                                <div className="flex flex-row items-end gap-2 mt-2 justify-start">
                                    <Badge withoutRemove={true} text={automatisation.is_active ? translate('automatisation.published') : translate('automatisation.draft')} color={automatisation.is_active ? 'green' : 'gray'} />
                                </div>
                               
                            </div>}
                            {isCampaign && <div className="flex flex-col border-1 w-max h-min border-gray-800 rounded-md p-4 bg-white mt-8">
                                <h2 className="text-2xl font-bold">{translate('automatisation.start')}</h2>
                            </div>}
                            <LineDown previousCloneStep={null} nextCloneStep={stepMapped.find((step) => !step.automatisation_step_before_id)} stepToClone={stepToClone} withPlus={true} action={() => panelHook.setOpen(true, <ChooseStep previousStep={null} nextStep={stepMapped.find((step) => !step.automatisation_step_before_id)} automatisationId={automatisationId} panelHook={panelHook} refresh={refresh} apps={apps} userApps={userApps} setApps={setApps} />)} cloneAction={cloneStep} />
                            {stepMapped.map((step, index) => (
                                <>
                                <AutomatisationStep 
                                    cloneAction={cloneStep}
                                    stepToClone={stepToClone}
                                    setStepToClone={setStepToClone}
                                    automatisationId={automatisationId} 
                                    panelHook={panelHook} 
                                    step={step} 
                                    refresh={refresh} 
                                    deleteStep={deleteStep} 
                                    app={apps?.find((app) => app.id === userApps?.find((userApp) => userApp.id === step.user_app_id)?.app_id) } 
                                    userApp={userApps?.find((userApp) => userApp.id === step.user_app_id)} 
                                    appWebhookStep={hardcodedAppsWebhook.find((webhook) => webhook.webhooks.find((whStep) => whStep.webhook_unique_identifier.toLowerCase() === step.app_webhook_unique_identifier?.toLowerCase()))?.webhooks.find((whStep) => whStep.webhook_unique_identifier.toLowerCase() === step.app_webhook_unique_identifier?.toLowerCase())} 
                                    apps={apps} 
                                    userApps={userApps} 
                                    setApps={setApps} />
                                {step.type.toUpperCase() !== 'CONDITION' && <LineDown previousCloneStep={step} nextCloneStep={stepMapped.find((s) => s.automatisation_step_before_id === step.id)} cloneAction={cloneStep} stepToClone={stepToClone} withPlus={true} action={() => panelHook.setOpen(true, <ChooseStep previousStep={step} nextStep={stepMapped.find((s) => s.automatisation_step_before_id === step.id)} automatisationId={automatisationId} panelHook={panelHook} refresh={refresh} apps={apps} userApps={userApps} setApps={setApps} />)} />}
                                </>
                            ))}
                           
                            <div className="flex flex-col w-full h-full justify-center h-64 min-h-64" />
                            
                        </div>

                    </div>
                )}
            </SidebarNav>
            <div className="fixed bottom-0 right-0 bg-white px-4 py-2 rounded-md border-1 border-gray-200">
                <div className="flex flex-row items-center justify-center gap-2">
                    {isCampaign && <div>
                        <Button className="bg-green-500 text-white" onClick={runCampaign}>{translate('automatisation.run')}</Button>
                        <Button className="bg-red-500 text-white ml-2" onClick={stopCampaign}>{translate('automatisation.stop')}</Button>
                    </div>}
                    {!isCampaign && <div className="flex flex-row items-center justify-center gap-2 border-r border-gray-200 pr-2">
                        <Switch checked={automatisation?.is_active} onChange={publishAutomatisation} label={translate('automatisation.publish')} />    
                    </div>}
                    {!isCampaign && <div className="flex flex-row items-center justify-center gap-2">
                        <span className="text-gray-500 border-r border-gray-200 pr-2 cursor-pointer" onClick={() => setIsExitStrategyModalOpen(true)}>{translate('automatisation.exit_strategy')}</span>
                    </div>}
                    <span className="text-gray-500 border-r border-gray-200 pr-2">zoom</span>
                    <button className="rounded-md p-2" onClick={() => updateZoom(0.1)}>
                        <MagnifyingGlassPlusIcon className="size-5 text-gray-500" />
                    </button>
                    <button className="rounded-md p-2" onClick={() => updateZoom(-0.1)}>
                        <MagnifyingGlassMinusIcon className="size-5 text-gray-500" />
                    </button>
                </div>

            </div>
            {!isCampaign && false && <div className="fixed top-16 right-0 bg-white px-4 py-2 rounded-md border-1 border-gray-900">
                <div className="flex flex-col justify-center gap-2 p-4 max-w-96">
                    <h3 className="text-2xl font-bold">{translate('automatisation.exit_strategy')}</h3>
                    <p className="text-md text-gray-500">{translate('automatisation.exit_strategy_description')}</p>
                    <div className="flex flex-row items-center ">
                        <div className="flex flex-col min-w-48 max-w-48 justify-center">
                            <Select title={''} options={exitStategyTypes} selected={selectedExitStrategyType} setSelected={setSelectedExitStrategyType} />
                        </div>
                    </div>
                    {selectedExitStrategyType?.value === 'tag' && <div className="flex flex-row items-center ">
                        <div className="flex flex-col min-w-48 max-w-48 justify-center">
                            <Select title={''} options={tags} selected={exitTag} setSelected={setExitTag} />
                        </div>
                    </div>}
                    {selectedExitStrategyType?.value === 'event' && <div className="flex flex-row items-center ">
                        <div className="flex flex-col min-w-48 max-w-48 justify-center">
                            <TextInput title={translate('automatisation.exit_strategy_event_name')} value={automatisation?.exit_strategy_value} onChange={(value) => setAutomatisation({...automatisation, exit_strategy_value: value.target.value})} />
                        </div>
                    </div>}
                    <div className="flex flex-row items-center justify-end">
                        <Button className="bg-red-500 text-white" onClick={clearExitStrategy}>{translate('clear')}</Button>
                        <Button className="bg-green-500 text-white ml-2" onClick={saveExitStrategy}>{translate('save')}</Button>
                    </div>
                  
                </div>

            </div>}
            
            <Panel title={translate('automatisation.api_call')} panelHook={panelHook} />
        </div>

    );
}

export const ChooseStep = ({panelHook, refresh, automatisationId, previousStep, nextStep, isAddingStepAsTrueFlowOrigin, isAddingStepAsFalseFlowOrigin, apps, userApps, setApps}) => {
    const { translate } = useTranslation();


    const api = useAPI();
    const [appsInternal, setAppsInternal] = useState(apps);
    const { refreshId, refresh: refreshUI } = useRefresh();

    const onCreateNewStep = async (step, keepFlowForNewCondition = null) => {

        if (previousStep && previousStep.type.toUpperCase() === 'CONDITION') {
            if (isAddingStepAsTrueFlowOrigin) {
                previousStep.data.results.true_next_step_id = step.id;
            } else if (isAddingStepAsFalseFlowOrigin) {
                previousStep.data.results.false_next_step_id = step.id;
            }
            await api.updateAutomatisationStep(automatisationId, previousStep);
        }

        if (step && nextStep && step.type.toUpperCase() === 'CONDITION' && (keepFlowForNewCondition === true || keepFlowForNewCondition === false)) { 
            
            if (keepFlowForNewCondition === true) {
                step.data.results.true_next_step_id = nextStep.id;
            } else if (keepFlowForNewCondition === false) {
                step.data.results.false_next_step_id = nextStep.id;
            }

            await api.updateAutomatisationStep(automatisationId, step);

            nextStep.automatisation_step_before_id = step.id;
            await api.updateAutomatisationStep(automatisationId, nextStep); 
        } else if (step && nextStep) {
            nextStep.automatisation_step_before_id = step.id;
            await api.updateAutomatisationStep(automatisationId, nextStep);
        }
        
    }

    const handleWebhookClick = () => {
        panelHook.setOpen(false);
        setTimeout(() => {
            panelHook.setOpen(true, <APICallPanel onCreateNewStep={onCreateNewStep} panelHook={panelHook} step={{type: 'APICALL', automatisation_step_before_id: previousStep?.id, automatisation_id: automatisationId, data: {body: "{}", headers: {}, method: 'GET', url: ''}}} refresh={refresh} isCreate={true} />);
        }, 1000);
    }

    const handleConditionTagClick = () => {
        panelHook.setOpen(false);
        setTimeout(() => {
            panelHook.setOpen(true, <ConditionPanel onCreateNewStep={onCreateNewStep} panelHook={panelHook} step={{type: 'CONDITION', automatisation_step_before_id: previousStep?.id, automatisation_id: automatisationId, data: {operation: 'CONTACT_IS_IN_TAG', variable: '', value: '', variable_type: '', results: {true_next_step_id: null, false_next_step_id: null}}}} refresh={refresh} isCreate={true} />);
        }, 1000);
    }

    const handleConditionEventClick = () => {
        panelHook.setOpen(false);
        setTimeout(() => {
            panelHook.setOpen(true, <ConditionPanel onCreateNewStep={onCreateNewStep} panelHook={panelHook} step={{type: 'CONDITION', automatisation_step_before_id: previousStep?.id, automatisation_id: automatisationId, data: {operation: 'EQUALS', variable: 'event.', value: '', variable_type: 'number', results: {true_next_step_id: null, false_next_step_id: null}}}} refresh={refresh} isCreate={true} />);
        }, 1000);
    }

    const handleConditionContactClick = () => {
        panelHook.setOpen(false);
        setTimeout(() => {
            panelHook.setOpen(true, <ConditionPanel onCreateNewStep={onCreateNewStep} panelHook={panelHook} step={{type: 'CONDITION', automatisation_step_before_id: previousStep?.id, automatisation_id: automatisationId, data: {operation: 'EQUALS', variable: 'contact.', value: '', variable_type: 'number', results: {true_next_step_id: null, false_next_step_id: null}}}} refresh={refresh} isCreate={true} />);
        }, 1000);
    }

    const handleConditionGlobalClick = () => {
        panelHook.setOpen(false);
        setTimeout(() => {
            panelHook.setOpen(true, <ConditionPanel onCreateNewStep={onCreateNewStep} panelHook={panelHook} step={{type: 'CONDITION', automatisation_step_before_id: previousStep?.id, automatisation_id: automatisationId, data: {operation: 'EQUALS', variable: 'global.', value: '', variable_type: 'number', results: {true_next_step_id: null, false_next_step_id: null}}}} refresh={refresh} isCreate={true} />);
        }, 1000);
    }

    const handleDelayClick = () => {
        panelHook.setOpen(false);
        setTimeout(() => {
            panelHook.setOpen(true, <DelayPanel onCreateNewStep={onCreateNewStep} panelHook={panelHook} step={{type: 'DELAY', automatisation_step_before_id: previousStep?.id, automatisation_id: automatisationId, data: {delay: 0, use_variable: false, delay_variable: 'event.'}}} refresh={refresh} isCreate={true} />);
        }, 1000);
    }

    const handleUpdateVariableClick = () => {
        panelHook.setOpen(false);
        setTimeout(() => {
            panelHook.setOpen(true, <UpdateVariablePanel onCreateNewStep={onCreateNewStep} panelHook={panelHook} step={{type: 'UPDATE_VARIABLE', automatisation_step_before_id: previousStep?.id, automatisation_id: automatisationId, data: {variable: 'event.', value: '', operation: 'ADDITION'}}} refresh={refresh} isCreate={true} />);
        }, 1000);
    }

    const handleDateClick = () => {
        panelHook.setOpen(false);
        setTimeout(() => {
            panelHook.setOpen(true, <DatePanel onCreateNewStep={onCreateNewStep} panelHook={panelHook} step={{type: 'DELAY', automatisation_step_before_id: previousStep?.id, automatisation_id: automatisationId, data: {delay: 0, use_variable: false, delay_variable: 'event.', use_date_variable: false, date: new Date()}}} refresh={refresh} isCreate={true} />);
        }, 1000);
    }

    const handleTagClick = () => {
        panelHook.setOpen(false);
        setTimeout(() => {
            panelHook.setOpen(true, <TagPanel onCreateNewStep={onCreateNewStep} panelHook={panelHook} step={{type: 'TAG', automatisation_step_before_id: previousStep?.id, automatisation_id: automatisationId, data: {is_deletion: false}}} refresh={refresh} isCreate={true} />);
        }, 1000);
    }

    const handleLoopClick = () => {
        panelHook.setOpen(false);
        setTimeout(() => {
            panelHook.setOpen(true, <LoopPanel onCreateNewStep={onCreateNewStep} panelHook={panelHook} step={{type: 'LOOP', automatisation_step_before_id: previousStep?.id, automatisation_id: automatisationId, data: {source: 'contact'}}} refresh={refresh} isCreate={true} />);
        }, 1000);
    }

    const handleAppClick = (app, userApps) => {
        if (userApps.some((userApp) => userApp.app_id === app.id)) {
            const newApps = appsInternal.map((a) => a.id === app.id ? { ...a, open: !a.open, webhooks: hardcodedAppsWebhook.find((webhook) => webhook.name.toLowerCase() === a.name.toLowerCase())?.webhooks || [] } : a);
            setApps(newApps);
            setAppsInternal(newApps);
        }
        
    }

    const handleTrackClick = () => {
        panelHook.setOpen(false);
        setTimeout(() => {
            panelHook.setOpen(true, <TrackPanel onCreateNewStep={onCreateNewStep} panelHook={panelHook} step={{type: 'TRACK', automatisation_step_before_id: previousStep?.id, automatisation_id: automatisationId, data: {event_name: '', event_data: {}}}} refresh={refresh} isCreate={true} />);
        }, 1000);
    }

    const handleCountEventClick = () => {
        panelHook.setOpen(false);
        setTimeout(() => {
            panelHook.setOpen(true, <CountPanel onCreateNewStep={onCreateNewStep} panelHook={panelHook} step={{type: 'COUNT', automatisation_step_before_id: previousStep?.id, automatisation_id: automatisationId, data: {type: 'event', timeframe: 1, timeframe_unit: 'MINUTES', contact_external_id: '', value: '', filters: []}}} refresh={refresh} isCreate={true} />);
        }, 1000);
    }

    const handleCountItemsClick = () => {
        panelHook.setOpen(false);
        setTimeout(() => {
            panelHook.setOpen(true, <CountPanel onCreateNewStep={onCreateNewStep} panelHook={panelHook} step={{type: 'COUNT', automatisation_step_before_id: previousStep?.id, automatisation_id: automatisationId, data: {type: 'items', value: 'event.',}}} refresh={refresh} isCreate={true} />);
        }, 1000);
    }

    const handleGoToClick = () => {
        panelHook.setOpen(false);
        setTimeout(() => {
            panelHook.setOpen(true, <GoToPanel onCreateNewStep={onCreateNewStep} panelHook={panelHook} automatisationId={automatisationId} step={{type: 'GOTO', automatisation_step_before_id: previousStep?.id, automatisation_id: automatisationId, data: {go_to_next_step_id: null}}} refresh={refresh} isCreate={true} />);
        }, 1000);
    }


    const renderConditionsTab = useCallback(() => {
        return (
            <div className="flex flex-col mt-8 gap-2">
                <div onClick={handleConditionTagClick} className="flex flex-row items-center cursor-pointer hover:bg-gray-100 rounded-md p-2 pb-4 border-b border-gray-200">
                    <img src={split} alt="condition" className={`w-12 h-12 min-w-12 min-h-12 aspect-square border p-2 bg-gray-50 rounded-md cursor-pointer mr-2 ${textColor}`} />
                    <div className="flex flex-col justify-center h-full ml-2">
                        <h2 className="text-xl font-bold leading-[1]">{translate('automatisation.step_condition_tag')}</h2>
                        <p className="text-gray-600 font-medium text-wrap text-sm break-all">{translate('automatisation.step_condition_tag_description')}</p>
                    </div>
                </div>
                <div onClick={handleConditionEventClick} className="flex flex-row items-center cursor-pointer hover:bg-gray-100 rounded-md p-2 pb-4 border-b border-gray-200">
                    <img src={split} alt="condition" className={`w-12 h-12 min-w-12 min-h-12 aspect-square border p-2 bg-gray-50 rounded-md cursor-pointer mr-2 ${textColor}`} />
                    <div className="flex flex-col justify-center h-full ml-2">
                        <h2 className="text-xl font-bold leading-[1]">{translate('automatisation.step_condition_event')}</h2>
                        <p className="text-gray-600 font-medium text-wrap text-sm break-all">{translate('automatisation.step_condition_event_description')}</p>
                    </div>
                </div>
                <div onClick={handleConditionContactClick} className="flex flex-row items-center cursor-pointer hover:bg-gray-100 rounded-md p-2 pb-4 border-b border-gray-200">
                    <img src={split} alt="condition" className={`w-12 h-12 min-w-12 min-h-12 aspect-square border p-2 bg-gray-50 rounded-md cursor-pointer mr-2 ${textColor}`} />
                    <div className="flex flex-col justify-center h-full ml-2">
                        <h2 className="text-xl font-bold leading-[1]">{translate('automatisation.step_condition_contact')}</h2>
                        <p className="text-gray-600 font-medium text-wrap text-sm break-all">{translate('automatisation.step_condition_contact_description')}</p>
                    </div>
                </div>
                <div onClick={handleConditionGlobalClick} className="flex flex-row items-center cursor-pointer hover:bg-gray-100 rounded-md p-2 pb-4 border-b border-gray-200">
                    <img src={split} alt="condition" className={`w-12 h-12 min-w-12 min-h-12 aspect-square border p-2 bg-gray-50 rounded-md cursor-pointer mr-2 ${textColor}`} />
                    <div className="flex flex-col justify-center h-full ml-2">
                        <h2 className="text-xl font-bold leading-[1]">{translate('automatisation.step_condition_global')}</h2>
                        <p className="text-gray-600 font-medium text-wrap text-sm break-all">{translate('automatisation.step_condition_global_description')}</p>
                    </div>
                </div>
            </div>
        )
    }, []);

    const renderActionsTab = useCallback(() => {
        return (
        <div className="flex flex-col mt-8 gap-2">
            <div onClick={handleWebhookClick} className="flex flex-row items-center cursor-pointer hover:bg-gray-100 rounded-md p-2 pb-4 border-b border-gray-200">
                <img src={webhook} alt="condition" className={`w-12 h-12 min-w-12 min-h-12 aspect-square border p-2 bg-gray-50 rounded-md cursor-pointer mr-2 ${textColor}`} />
                <div className="flex flex-col justify-center h-full ml-2">
                    <h2 className="text-xl font-bold leading-[1]">{translate('automatisation.step_webhook_custom')}</h2>
                    <p className="text-gray-600 font-medium text-wrap text-sm break-all">{translate('automatisation.step_webhook_custom_description')}</p>
                </div>
            </div>
            <div onClick={handleDelayClick} className="flex flex-row items-center cursor-pointer hover:bg-gray-100 rounded-md p-2 pb-4 border-b border-gray-200">
                <ClockIcon className={`w-12 h-12 min-w-12 min-h-12 aspect-square border p-2 bg-gray-50 rounded-md cursor-pointer mr-2 ${textColor}`} />
                <div className="flex flex-col justify-center h-full ml-2">
                    <h2 className="text-xl font-bold leading-[1]">{translate('automatisation.step_delay')}</h2>
                    <p className="text-gray-600 font-medium text-wrap text-sm break-all">{translate('automatisation.step_delay_description')}</p>
                </div>
            </div>
            <div onClick={handleDateClick} className="flex flex-row items-center cursor-pointer hover:bg-gray-100 rounded-md p-2 pb-4 border-b border-gray-200">
                <CalendarIcon className={`w-12 h-12 min-w-12 min-h-12 aspect-square border p-2 bg-gray-50 rounded-md cursor-pointer mr-2 ${textColor}`} />
                <div className="flex flex-col justify-center h-full ml-2">
                    <h2 className="text-xl font-bold leading-[1]">{translate('automatisation.step_date')}</h2>
                    <p className="text-gray-600 font-medium text-wrap text-sm break-all">{translate('automatisation.step_date_description')}</p>
                </div>
            </div>
            <div onClick={handleUpdateVariableClick} className="flex flex-row items-center cursor-pointer hover:bg-gray-100 rounded-md p-2 pb-4 border-b border-gray-200">
                <VariableIcon className={`w-12 h-12 min-w-12 min-h-12 aspect-square border p-2 bg-gray-50 rounded-md cursor-pointer mr-2 ${textColor}`} />
                <div className="flex flex-col justify-center h-full ml-2">
                    <h2 className="text-xl font-bold leading-[1]">{translate('automatisation.step_update_variable')}</h2>
                    <p className="text-gray-600 font-medium text-wrap text-sm break-all">{translate('automatisation.step_update_variable_description')}</p>
                </div>
            </div>
            <div onClick={handleTagClick} className="flex flex-row items-center cursor-pointer hover:bg-gray-100 rounded-md p-2 pb-4 border-b border-gray-200">
                <TagIcon className={`w-12 h-12 min-w-12 min-h-12 aspect-square border p-2 bg-gray-50 rounded-md cursor-pointer mr-2 ${textColor}`} />
                <div className="flex flex-col justify-center h-full ml-2">
                    <h2 className="text-xl font-bold leading-[1]">{translate('automatisation.step_tag')}</h2>
                    <p className="text-gray-600 font-medium text-wrap text-sm break-all">{translate('automatisation.step_tag_description')}</p>
                </div>
            </div>
            <div onClick={handleLoopClick} className="flex flex-row items-center cursor-pointer hover:bg-gray-100 rounded-md p-2 pb-4 border-b border-gray-200">
                <img src={iteration} alt="iteration" className={`w-12 h-12 min-w-12 min-h-12 aspect-square border p-2 bg-gray-50 rounded-md cursor-pointer mr-2 ${textColor}`} />
                <div className="flex flex-col justify-center h-full ml-2">
                    <h2 className="text-xl font-bold leading-[1]">{translate('automatisation.step_loop')}</h2>
                    <p className="text-gray-600 font-medium text-wrap text-sm break-all">{translate('automatisation.step_loop_description')}</p>
                </div>
            </div>
            <div onClick={handleTrackClick} className="flex flex-row items-center cursor-pointer hover:bg-gray-100 rounded-md p-2 pb-4 border-b border-gray-200">
                <DocumentPlusIcon className={`w-12 h-12 min-w-12 min-h-12 aspect-square border p-2 bg-gray-50 rounded-md cursor-pointer mr-2 ${textColor}`} />
                <div className="flex flex-col justify-center h-full ml-2">
                    <h2 className="text-xl font-bold leading-[1]">{translate('automatisation.step_track')}</h2>
                    <p className="text-gray-600 font-medium text-wrap text-sm break-all">{translate('automatisation.step_track_description')}</p>
                </div>
            </div>
            <div onClick={handleCountEventClick} className="flex flex-row items-center cursor-pointer hover:bg-gray-100 rounded-md p-2 pb-4 border-b border-gray-200">
                <CalculatorIcon className={`w-12 h-12 min-w-12 min-h-12 aspect-square border p-2 bg-gray-50 rounded-md cursor-pointer mr-2 ${textColor}`} />
                <div className="flex flex-col justify-center h-full ml-2">
                    <h2 className="text-xl font-bold leading-[1]">{translate('automatisation.step_count_event')}</h2>
                    <p className="text-gray-600 font-medium text-wrap text-sm break-all">{translate('automatisation.step_count_event_description')}</p>
                </div>
            </div>
            <div onClick={handleCountItemsClick} className="flex flex-row items-center cursor-pointer hover:bg-gray-100 rounded-md p-2 pb-4 border-b border-gray-200">
                <CalculatorIcon className={`w-12 h-12 min-w-12 min-h-12 aspect-square border p-2 bg-gray-50 rounded-md cursor-pointer mr-2 ${textColor}`} />
                <div className="flex flex-col justify-center h-full ml-2">
                    <h2 className="text-xl font-bold leading-[1]">{translate('automatisation.step_count_items')}</h2>
                    <p className="text-gray-600 font-medium text-wrap text-sm break-all">{translate('automatisation.step_count_items_description')}</p>
                </div>
            </div>
            <div onClick={handleGoToClick} className="flex flex-row items-center cursor-pointer hover:bg-gray-100 rounded-md p-2 pb-4 border-b border-gray-200">
                <ArrowRightStartOnRectangleIcon className={`w-12 h-12 min-w-12 min-h-12 aspect-square border p-2 bg-gray-50 rounded-md cursor-pointer mr-2 ${textColor}`} />
                <div className="flex flex-col justify-center h-full ml-2">
                    <h2 className="text-xl font-bold leading-[1]">{translate('automatisation.step_go_to')}</h2>
                    <p className="text-gray-600 font-medium text-wrap text-sm break-all">{translate('automatisation.step_go_to_description')}</p>
                </div>
            </div>
        </div>
        )
    }, []);

    const renderAppsTab = useCallback((appsInternal, userApps) => {
        return (
            <div className="flex flex-col mt-8 gap-2">
               {appsInternal.map((app) => (
                    <div onClick={() => {handleAppClick(app, userApps); refreshUI()}} key={app.id} className={`flex flex-1 w-full flex-col items-center justify-between `}>
                        <div className={`flex flex-row items-center justify-between w-full border p-4 border-gray-200 rounded-md ${userApps.some((userApp) => userApp.app_id === app.id) ? 'hover:bg-gray-100 opacity-100 cursor-pointer' : 'opacity-50'}`}>
                            <p className="text-black font-bold text-lg mt-2">{app.name} ({hardcodedAppsWebhook.find((webhook) => webhook.name.toLowerCase() === app.name.toLowerCase())?.webhooks.length || 0} {translate('automatisation.step_apps_webhooks')})</p>
                            <div className="flex flex-row items-center gap-2">
                                <Badge 
                                    withoutRemove={true} 
                                    color={userApps.some((userApp) => userApp.app_id === app.id) ? 'green' : 'gray'} 
                                    text={userApps.some((userApp) => userApp.app_id === app.id) ? translate('automatisation.step_apps_configured') : translate('automatisation.step_apps_not_configured')} 
                                />
                                {userApps.some((userApp) => userApp.app_id === app.id) && !app.open && <ChevronDownIcon className="size-4 text-gray-500" />}
                                {userApps.some((userApp) => userApp.app_id === app.id) && app.open && <ChevronUpIcon className="size-4 text-gray-500" />}
                            </div> 
                        </div>
                        
                        {app.open && <div className="flex flex-col mt-2 gap-2 w-full">
                            {app?.webhooks?.map((webhook) => (
                            <div onClick={() => webhook.onClick(panelHook, onCreateNewStep, refresh, automatisationId, previousStep, app, webhook, userApps.find((userApp) => userApp.app_id === app.id))}  className="flex flex-row items-center cursor-pointer hover:bg-gray-100 rounded-md p-2 pb-4 border-b border-gray-200">
                                <img src={app.icon} alt="API Call" className={`w-12 h-12 min-w-12 min-h-12 aspect-square border p-2 bg-gray-50 rounded-md cursor-pointer mr-2 border-gray-200`} />
                                <div className="flex flex-col justify-center h-full ml-2">
                                    <h2 className="text-xl font-bold leading-[1] text-black">{webhook.name}</h2>
                                    <p className="text-gray-600 font-medium text-wrap text-sm break-all">{webhook.description}</p>
                                </div>
                            </div>))}
                        </div>}
                    </div>
                ))}
            </div>
        )
    }, [appsInternal, apps, userApps, refreshId]);

    const tabs = [
        { name: translate('automatisation.actions'),  current: true, render: renderActionsTab },
        { name: translate('automatisation.conditions'), current: false, render: renderConditionsTab },
        { name: translate('automatisation.step_apps'), current: false, render: () => renderAppsTab(appsInternal, userApps) },
    ]


    const tabState = useTabs(tabs);

    const selectedTab = tabState.tabs.find((tab) => tab.current);

    useEffect(() => {
        tabState.setTabs([...tabs.map((tab) => ({...tab, current: tab.name === selectedTab?.name}))]);
    }, [apps, appsInternal, userApps]);

    return (
        <div>
            <h2 className="text-2xl font-bold">{translate('automatisation.step_type')}</h2>

            <div className="mt-4">
                <SegmentControl tabs={tabState} />
            </div>

            {selectedTab.render()}

            {/* <h3 className="text-xl text-gray-900 font-bold mt-4 border-b border-gray-200 pb-1">{translate('automatisation.step_apps')}</h3>
            <div className="grid grid-cols-1 gap-4 mt-4">
                {appsInternal.map((app) => (
                    <div onClick={() => handleAppClick(app, userApps)} key={app.id} className={`flex flex-1 w-full flex-col items-center justify-between `}>
                        <div className={`flex flex-row items-center justify-between w-full border p-4 border-gray-200 rounded-md ${userApps.some((userApp) => userApp.app_id === app.id) ? 'hover:bg-gray-100 opacity-100 cursor-pointer' : 'opacity-50'}`}>
                            <p className="text-black font-bold text-lg mt-2">{app.name} ({hardcodedAppsWebhook.find((webhook) => webhook.name.toLowerCase() === app.name.toLowerCase())?.webhooks.length || 0} {translate('automatisation.step_apps_webhooks')})</p>
                            <div className="flex flex-row items-center gap-2">
                                <Badge 
                                    withoutRemove={true} 
                                    color={userApps.some((userApp) => userApp.app_id === app.id) ? 'green' : 'gray'} 
                                    text={userApps.some((userApp) => userApp.app_id === app.id) ? translate('automatisation.step_apps_configured') : translate('automatisation.step_apps_not_configured')} 
                                />
                                {userApps.some((userApp) => userApp.app_id === app.id) && !app.open && <ChevronDownIcon className="size-4 text-gray-500" />}
                                {userApps.some((userApp) => userApp.app_id === app.id) && app.open && <ChevronUpIcon className="size-4 text-gray-500" />}
                            </div> 
                        </div>
                        
                        {app.open &&  <div className="grid grid-cols-2 gap-4 w-full">
                            {app?.webhooks?.map((webhook) => (
                                <div onClick={() => webhook.onClick(panelHook, onCreateNewStep, refresh, automatisationId, previousStep, app, webhook, userApps.find((userApp) => userApp.app_id === app.id))} key={webhook.name} className="flex flex-1 w-full flex-col items-center justify-center border-2 border-gray-200 rounded-md p-4 mt-4 aspect-square cursor-pointer hover:bg-gray-100">
                                    <img src={app.icon} alt="API Call" className="size-16 object-contain" />
                                    <p className="text-gray-500 font-bold text-center text-lg mt-2">{webhook.name}</p>
                                </div>
                            ))}
                        </div>}
                    </div>
                ))}
            </div>
            <h3 className="text-xl text-gray-900 font-bold mt-4 border-b border-gray-200 pb-1">{translate('automatisation.step_webhook')}</h3>
           

            <h3 className="text-xl text-gray-900 font-bold mt-4 border-b border-gray-200 pb-1">{translate('automatisation.step_condition')}</h3>
           

            <h3 className="text-xl text-gray-900 font-bold mt-8 border-b border-gray-200 pb-1">{translate('automatisation.step_actions')}</h3>
            <div className="grid grid-cols-2 gap-4">
               
            </div> */}
        </div>
    )
}